import {supabase} from './supabase';
import {getPrimaryMembership} from './agentordinaryWorkspace';
import type {Agent, CharacterProfile, CharacterBaseType, CharacterAppearance} from '../types';

/** Owner-only workforce controls. Global resume never overrides agent or safety-specific holds. */
async function ensureOwner(workspaceId:string){
 const membership=await getPrimaryMembership();
 if(membership.workspace_id!==workspaceId || membership.role!=='owner'){
  throw new Error('Only this workspace owner can change workforce controls.');
 }
}
export async function pauseEntireWorkforce(workspaceId:string):Promise<void>{
 await ensureOwner(workspaceId);
 const {data,error}=await supabase.rpc('agentordinary_owner_pause_workforce',{p_workspace_id:workspaceId});
 if(error)throw error;
 if(data!==true)throw new Error('Global pause was not confirmed by the database.');
}
export async function resumeEntireWorkforce(workspaceId:string):Promise<void>{
 await ensureOwner(workspaceId);
 const {data,error}=await supabase.rpc('agentordinary_owner_resume_workforce',{p_workspace_id:workspaceId});
 if(error)throw error;
 if(data!==true)throw new Error('Global resume was not confirmed by the database.');
}
export async function pauseOneEmployee(workspaceId:string,agentId:string):Promise<void>{
 await ensureOwner(workspaceId);
 const {data,error}=await supabase.rpc('agentordinary_owner_pause_employee',{p_workspace_id:workspaceId,p_agent_id:agentId});
 if(error)throw error;
 if(data!==true)throw new Error('Employee pause was not confirmed by the database.');
}
/** Owner-authenticated Run now for an existing employee; never bypasses the runtime safety gate. */
export async function runEmployeeNow(
 workspaceId:string,agentId:string,instruction:string,dispatchId:string
):Promise<{taskId:string;status:string}>{
 await ensureOwner(workspaceId);
 const clean=instruction.trim();
 if(clean.length<4||clean.length>2400)throw new Error('Enter an instruction between 4 and 2400 characters.');
 if(!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(dispatchId))
  throw new Error('Invalid run request identifier.');
 const {data,error}=await supabase.rpc('agentordinary_owner_dispatch_task_v1',{
  p_workspace_id:workspaceId,p_agent_id:agentId,p_dispatch_id:dispatchId,p_instruction:clean
 });
 if(error)throw error;
 if(data?.ok!==true||!data?.task_id)throw new Error(String(data?.error||'Run was not confirmed by the database.'));
 return {taskId:String(data.task_id),status:String(data.status||'queued')};
}

export async function updateEmployeeNickname(workspaceId:string,agent:Agent,nickname:string):Promise<string>{
 await ensureOwner(workspaceId);
 const clean=nickname.trim().replace(/\s+/g,' ');
 if(clean.length>40)throw new Error('Nickname must be 40 characters or fewer.');
 const {data,error}=await supabase.rpc('agentordinary_owner_update_nickname',{
  p_workspace_id:workspaceId,p_agent_id:agent.id,p_nickname:clean,p_expected_revision:agent.revision
 });
 if(error)throw error;
 return String(data||'');
}

/** Account-scoped visual customization. Does not touch execution permissions or skills. */
export async function saveEmployeeCharacterProfile(
 workspaceId:string,agentId:string,baseType:CharacterBaseType,appearance:CharacterAppearance
):Promise<CharacterProfile>{
 await ensureOwner(workspaceId);
 const {data,error}=await supabase.rpc('agentordinary_owner_save_character_profile',{
  p_workspace_id:workspaceId,p_agent_id:agentId,p_base_type:baseType,p_appearance:appearance
 });
 if(error)throw error;
 if(!data||data.agent_id!==agentId||data.workspace_id!==workspaceId){
  throw new Error('Character profile was not saved to the selected workspace.');
 }
 return {id:String(data.id),workspace_id:workspaceId,agent_id:agentId,
  base_type:data.base_type as CharacterBaseType,
  appearance:data.appearance as CharacterAppearance,
  transparent_background:true,status:data.status==='ready'?'ready':'draft',
  created_at:String(data.created_at),updated_at:String(data.updated_at)};
}

/** Owner-edited skills, training and identity stay in this user's workspace. */
export async function saveEmployeeDefinition(workspaceId:string,agent:Agent):Promise<Agent>{
 await ensureOwner(workspaceId);
 if(agent.workspace_id!==workspaceId)throw new Error('Employee belongs to a different workspace.');
 const {data,error}=await supabase.rpc('agentordinary_owner_update_employee_definition_v1',{
  p_workspace_id:workspaceId,p_agent_id:agent.id,p_expected_revision:agent.revision,
  p_official_name:agent.official_name,p_nickname:agent.nickname,
  p_title:agent.title,p_department:agent.department,p_profile:agent.profile
 });
 if(error)throw error;
 if(!data||data.id!==agent.id||data.workspace_id!==workspaceId)
   throw new Error('Employee definition update was not confirmed in this workspace.');
 return data as Agent;
}
